// src/pages/components/TaskFilters.tsx
import React, { useState, useEffect } from 'react';
import { Filter, ChevronDown } from 'lucide-react';
import { TaskFilters } from '../api/tasks';
import { clientsApi, Client } from '../api/clients';
import { vendorsApi, Vendor } from '../api/vendors';
import { projectsApi, Project } from '../api/projects';
import { getAllUsers, User } from '../api/user';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import PersonAvatar from './people/PersonAvatar';

interface TaskFiltersProps {
  filters: TaskFilters;
  onFilterChange: (filters: Partial<TaskFilters>) => void;
}

function TaskFiltersComponent({ filters, onFilterChange }: TaskFiltersProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [employees, setEmployees] = useState<User[]>([]);
  const [localFilters, setLocalFilters] = useState({
    status: filters.status || '',
    priority: filters.priority || '',
    assigned_to: filters.assigned_to || '',
    search: filters.search || '',
    client_id: (filters as any).client_id || '',
    vendor_id: (filters as any).vendor_id || '',
    project_id: (filters as any).project_id || '',
  });

  useEffect(() => {
    clientsApi.list({ page_size: 200 }).then(r => setClients(r.data?.items || [])).catch(() => {});
    vendorsApi.list({ page_size: 200 }).then(r => setVendors(r.data?.items || [])).catch(() => {});
    projectsApi.list({ page_size: 200 }).then(r => setProjects(r.data?.items || [])).catch(() => {});
    getAllUsers().then(r => setEmployees(r.users || [])).catch(() => {});
  }, []);

  const setFilterValue = (key: string, value: string) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    const nextValue = type === 'checkbox' ? checked : value;
    // Local state only — nothing fires until the user clicks Apply Filters.
    setLocalFilters(prev => ({ ...prev, [name]: nextValue }));
  };

  const handleApply = () => {
    const appliedFilters: Partial<TaskFilters> = {};
    if (localFilters.status) appliedFilters.status = localFilters.status;
    if (localFilters.priority) appliedFilters.priority = localFilters.priority;
    if (localFilters.assigned_to) appliedFilters.assigned_to = localFilters.assigned_to;
    if (localFilters.search) appliedFilters.search = localFilters.search;
    if (localFilters.client_id) (appliedFilters as any).client_id = localFilters.client_id;
    if (localFilters.vendor_id) (appliedFilters as any).vendor_id = localFilters.vendor_id;
    if (localFilters.project_id) (appliedFilters as any).project_id = localFilters.project_id;
    onFilterChange(appliedFilters);

    // Collapse the panel once filters are applied.
    setIsExpanded(false);
  };

  const handleReset = () => {
    setLocalFilters(prev => ({
      ...prev,
      status: '',
      priority: '',
      assigned_to: '',
      search: '',
      client_id: '',
      vendor_id: '',
      project_id: '',
    }));
    onFilterChange({
      status: undefined,
      priority: undefined,
      assigned_to: undefined,
      search: undefined,
      client_id: undefined,
      vendor_id: undefined,
      project_id: undefined,
    } as any);
  };

  const activeFiltersCount = [
    localFilters.status,
    localFilters.priority,
    localFilters.assigned_to,
    localFilters.search,
    localFilters.client_id,
    localFilters.vendor_id,
    localFilters.project_id,
  ].filter(Boolean).length;
  const hasPendingFilterChanges =
    localFilters.status !== (filters.status || '') ||
    localFilters.priority !== (filters.priority || '') ||
    localFilters.assigned_to !== (filters.assigned_to || '') ||
    localFilters.search !== (filters.search || '') ||
    localFilters.client_id !== ((filters as any).client_id || '') ||
    localFilters.vendor_id !== ((filters as any).vendor_id || '') ||
    localFilters.project_id !== ((filters as any).project_id || '');

  return (
    <div className="gt-card overflow-hidden">
      <button
        type="button"
        className="flex justify-between items-center p-4 w-full transition-colors"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <span style={{ width: 32, height: 32, borderRadius: 11, background: 'var(--gt-primary-container)', color: 'var(--gt-primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <Filter className="w-3.5 h-3.5" />
          </span>
          <span className="gt-display" style={{ fontSize: 14, fontWeight: 700, color: 'var(--gt-text-primary)' }}>Filters</span>
          {activeFiltersCount > 0 && (
            <span className="gt-pill is-personal gt-numerals">
              {activeFiltersCount} active
            </span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform`} style={{ color: 'var(--gt-text-tertiary)', transform: isExpanded ? 'rotate(180deg)' : 'none' }} />
      </button>

      {isExpanded && (
        <div className="p-5 space-y-5 gt-fade-in" style={{ borderTop: '1px solid var(--gt-divider)', background: 'var(--gt-surface-1)' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="gt-field-label">Status</label>
              <Select
                value={localFilters.status || "__all__"}
                onValueChange={(v) => setFilterValue('status', v === "__all__" ? "" : v)}
              >
                <SelectTrigger><SelectValue placeholder="All Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Status</SelectItem>
                  <SelectItem value="TODO">To Do</SelectItem>
                  <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                  <SelectItem value="IN_REVIEW">In Review</SelectItem>
                  <SelectItem value="BLOCKED">Hold</SelectItem>
                  <SelectItem value="DONE">Done</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="gt-field-label">Priority</label>
              <Select
                value={localFilters.priority || "__all__"}
                onValueChange={(v) => setFilterValue('priority', v === "__all__" ? "" : v)}
              >
                <SelectTrigger><SelectValue placeholder="All Priority" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Priority</SelectItem>
                  <SelectItem value="LOW">Low</SelectItem>
                  <SelectItem value="MEDIUM">Medium</SelectItem>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="URGENT">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="gt-field-label">Assigned To</label>
              <Select
                value={localFilters.assigned_to || "__all__"}
                onValueChange={(v) => setFilterValue('assigned_to', v === "__all__" ? "" : v)}
              >
                <SelectTrigger><SelectValue placeholder="All Employees" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Employees</SelectItem>
                  {employees.map((u) => {
                    const label = u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
                    return (
                      // textValue keeps Radix typeahead / native-option text on the name, not the avatar.
                      <SelectItem
                        key={u.user_id || u.id || ''}
                        value={(u.user_id || u.id) || ''}
                        textValue={`${label || ''}${u.role ? ` · ${u.role}` : ''}`}
                      >
                        <span className="inline-flex items-center gap-2">
                          <PersonAvatar userId={u.user_id ?? u.id} name={label} size={20} />
                          <span>
                            {label}
                            {u.role ? ` · ${u.role}` : ''}
                          </span>
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="gt-field-label">Search</label>
              <input
                type="text"
                name="search"
                value={localFilters.search}
                onChange={handleChange}
                placeholder="Title, description, or task ID…"
                className="gt-input"
              />
            </div>

            {clients.length > 0 && (
              <div>
                <label className="gt-field-label">Client</label>
                <Select
                  value={localFilters.client_id || "__all__"}
                  onValueChange={(v) => setFilterValue('client_id', v === "__all__" ? "" : v)}
                >
                  <SelectTrigger><SelectValue placeholder="All Clients" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Clients</SelectItem>
                    {clients.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.clientName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {vendors.length > 0 && (
              <div>
                <label className="gt-field-label">Vendor</label>
                <Select
                  value={localFilters.vendor_id || "__all__"}
                  onValueChange={(v) => setFilterValue('vendor_id', v === "__all__" ? "" : v)}
                >
                  <SelectTrigger><SelectValue placeholder="All Vendors" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Vendors</SelectItem>
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.clientName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {projects.length > 0 && (
              <div>
                <label className="gt-field-label">Project</label>
                <Select
                  value={localFilters.project_id || "__all__"}
                  onValueChange={(v) => setFilterValue('project_id', v === "__all__" ? "" : v)}
                >
                  <SelectTrigger><SelectValue placeholder="All Projects" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Projects</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="flex flex-wrap justify-end items-center gap-3 pt-1">
            <button type="button" onClick={handleReset} className="gt-btn is-ghost">Reset</button>
            <button
              type="button"
              onClick={handleApply}
              disabled={!hasPendingFilterChanges}
              title={hasPendingFilterChanges ? "Apply filter changes" : "Change a filter to enable Apply"}
              className={`gt-btn is-primary transition-all ${
                hasPendingFilterChanges
                  ? "opacity-100 shadow-sm"
                  : "opacity-35 cursor-not-allowed"
              }`}
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TaskFiltersComponent;
